import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { UserName } from "@/components/UserName";
import { formatDateTime } from "@/lib/datetime";
import { GitCompareArrows, ShoppingCart, ArrowRightLeft, Search, RotateCcw, ChevronDown, Truck, Clock, User, Package, MapPin } from "lucide-react";

const KIND_TABS = [
  { key: "", label: "All", icon: GitCompareArrows },
  { key: "exit", label: "Shipments (SHP)", icon: ShoppingCart },
  { key: "transfer", label: "Transfers (TRF)", icon: ArrowRightLeft },
];

const STATUS_STYLE = {
  shipped: "bg-emerald-100 text-emerald-700", received: "bg-emerald-100 text-emerald-700",
  in_transit: "bg-indigo-100 text-indigo-700", partially_received: "bg-amber-100 text-amber-700",
  cancelled: "bg-slate-100 text-slate-500",
};

function Stat({ testid, value, label, tone }) {
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 min-w-[120px] ${tone || "border-slate-200 bg-white"}`}>
      <div className="font-display text-3xl font-black leading-none">{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-1.5">{label}</div>
    </div>
  );
}

function ShipmentRow({ s, expanded, onToggle }) {
  const isTransfer = s.kind === "transfer";
  const number = s.shipment_number || s.transfer_number || s.cart_number;
  const dest = isTransfer
    ? `${s.from_site_code || "?"} → ${s.to_site_code || "?"}`
    : (s.po_reference || (s.assignment_type || "").replace(/_/g, " ") || "—");
  const when = s.created_at || s.shipped_at || s.sent_at;
  const who = s.created_by || s.shipped_by || s.sent_by;
  return (
    <div className="border-t border-slate-100">
      <button data-testid={`mh-row-${s.id}`} onClick={onToggle}
        className="w-full grid grid-cols-1 md:grid-cols-[86px_1fr_1fr_150px_150px_70px_28px] gap-2 px-4 py-2.5 text-sm items-center text-left hover:bg-slate-50 transition-colors">
        <span className={`justify-self-start rounded-lg text-[10px] font-mono font-bold px-2 py-0.5 ${isTransfer ? "bg-indigo-100 text-indigo-700" : "bg-[#B01E28]/10 text-[#B01E28]"}`}>{isTransfer ? "TRF" : "SHP"}</span>
        <span className="font-mono font-semibold text-slate-800 truncate flex items-center gap-1.5">{number}{s.name ? <span className="text-xs font-sans font-normal text-slate-400 truncate">· {s.name}</span> : null}</span>
        <span className="font-mono text-xs text-slate-600 truncate flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-300 shrink-0" />{dest}</span>
        <span className="font-mono text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3 text-slate-300 shrink-0" />{formatDateTime(when)}</span>
        <span className="text-xs text-slate-500 truncate flex items-center gap-1"><User className="w-3 h-3 text-slate-300 shrink-0" />{who ? <UserName email={who} /> : "—"}</span>
        <span className="flex items-center gap-2">
          <span className="font-mono text-xs text-slate-700 flex items-center gap-1"><Package className="w-3 h-3 text-slate-400" />{s.part_count}</span>
          {s.status ? <span className={`rounded text-[9px] font-mono uppercase px-1.5 py-0.5 ${STATUS_STYLE[s.status] || "bg-slate-100 text-slate-500"}`}>{(s.status || "").replace(/_/g, " ")}</span> : null}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-300 justify-self-end transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div data-testid={`mh-lines-${s.id}`} className="px-4 pb-3 pt-1 bg-slate-50/60">
          {(!s.parts || s.parts.length === 0) ? (
            <div className="text-xs text-slate-400 font-mono py-2">No part lines recorded.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
              {s.parts.map((p) => (
                <div key={p.part_id} data-testid={`mh-line-${p.part_id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                  <Package className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="font-mono text-xs font-semibold text-slate-700 shrink-0">{p.short_code || "—"}</span>
                  <span className="text-xs text-slate-500 truncate">{p.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MovementHistoryPage() {
  const [filters, setFilters] = useState({ kind: "", q: "", site: "", date_from: "", date_to: "" });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchPage = useCallback(async (p, replace) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v && String(v).trim()) params[k] = String(v).trim(); });
      const { data } = await api.get("/shipments", { params });
      setRows((prev) => (replace ? (data.shipments || []) : [...prev, ...(data.shipments || [])]));
      setHasMore(!!data.has_more);
      setTotal(data.count || 0);
      setPage(p);
    } catch { if (replace) { setRows([]); setTotal(0); setHasMore(false); } }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { const t = setTimeout(() => fetchPage(1, true), 300); return () => clearTimeout(t); }, [fetchPage]);
  useEffect(() => { api.get("/shipments/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const clearFilters = () => setFilters({ kind: "", q: "", site: "", date_from: "", date_to: "" });
  const active = Object.values(filters).some((v) => v && String(v).trim());

  return (
    <div data-testid="movement-history-page">
      <div className="flex items-center gap-2 mb-1">
        <GitCompareArrows className="w-5 h-5 text-[#B01E28]" />
        <h1 className="font-display text-2xl font-bold tracking-tight">Where Did It Go?</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">One timeline for every physical exit — cart shipments (SHP) and site transfers (TRF). Read-only: this traces where a part went, when, and who sent it.</p>

      {/* YARD VELOCITY */}
      {stats && (
        <div className="flex flex-wrap gap-3 mb-5">
          <Stat testid="stat-awaiting-truck" value={stats.awaiting_next_truck} label="Awaiting next truck" tone="border-[#E0781A]/40 bg-orange-50" />
          <Stat testid="stat-shipped-today" value={stats.shipped_today} label="Shipments today" />
          <Stat testid="stat-shipped-7d" value={stats.shipped_7d} label="Shipments · 7 days" />
          <Stat testid="stat-parts-shipped-7d" value={stats.parts_shipped_7d} label="Parts out · 7 days" />
        </div>
      )}

      {/* KIND TABS */}
      <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit mb-4">
        {KIND_TABS.map(({ key, label, icon: Icon }) => (
          <button key={key || "all"} data-testid={`mh-kind-${key || "all"}`} onClick={() => setFilters((f) => ({ ...f, kind: key }))}
            className={`flex items-center gap-1.5 text-sm px-4 py-2 transition-colors ${filters.kind === key ? "bg-[#E0781A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {/* FILTERS */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 p-4 border border-slate-200 bg-white rounded-xl">
        <div className="col-span-2 md:col-span-2"><Label className="text-[10px] uppercase tracking-wider text-slate-400">Search</Label>
          <div className="relative"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-300" />
            <Input data-testid="mh-filter-q" value={filters.q} onChange={set("q")} placeholder="SHP/TRF #, PO, name, sender…" className="rounded-lg border-slate-200 h-9 pl-8" /></div>
        </div>
        <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Destination site</Label>
          <Input data-testid="mh-filter-site" value={filters.site} onChange={set("site")} placeholder="e.g. RICE" className="rounded-lg border-slate-200 h-9 font-mono uppercase" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">From</Label>
          <Input data-testid="mh-filter-from" type="date" value={filters.date_from} onChange={set("date_from")} className="rounded-lg border-slate-200 h-9" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">To</Label>
          <Input data-testid="mh-filter-to" type="date" value={filters.date_to} onChange={set("date_to")} className="rounded-lg border-slate-200 h-9" /></div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-400" data-testid="mh-total">{loading && rows.length === 0 ? "Loading…" : `${total} shipment${total === 1 ? "" : "s"}`}</span>
        {active && <Button data-testid="mh-clear" variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-slate-500 hover:text-[#B01E28]"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Clear filters</Button>}
      </div>

      {/* LIST */}
      <div className="border border-slate-200 bg-white rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[86px_1fr_1fr_150px_150px_70px_28px] gap-2 bg-slate-100 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">
          <span>Kind</span><span>Number</span><span>Destination / PO</span><span>When</span><span>Sent by</span><span>Parts</span><span></span>
        </div>
        {rows.length === 0 && !loading ? (
          <div className="p-10 text-center font-mono text-xs text-slate-400" data-testid="mh-empty">
            <Truck className="w-6 h-6 mx-auto mb-2 text-slate-300" />NOTHING HAS SHIPPED YET
          </div>
        ) : rows.map((s) => (
          <ShipmentRow key={s.id} s={s} expanded={expanded === s.id} onToggle={() => setExpanded((x) => (x === s.id ? null : s.id))} />
        ))}
      </div>

      {hasMore && (
        <div className="text-center mt-4">
          <Button data-testid="mh-load-more" variant="outline" onClick={() => fetchPage(page + 1, false)} disabled={loading} className="rounded-lg border-slate-200">
            {loading ? "Loading…" : `Load more (${rows.length} of ${total})`}
          </Button>
        </div>
      )}
    </div>
  );
}
